/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  Button,
  Dropdown,
  FormGroup,
  Link,
  PasswordInput,
  TextInput
} from 'carbon-components-react';
import cx from 'classnames';
import _ from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  ELASTIC_SEARCH_AUTH_TYPES,
  ELASTIC_SEARCH_INDEX_INPUT_METHODS
} from '../../../../ts/skill';
import { SslNotification } from '../../../ia/components/notifications/SslNotification';
import { generateDocsUrl } from '../../../ia/util/docs';
import { REDACTED_FIELD } from '../../../ia/util/string';
import { HTTP_URL_REGEX } from '../../../ia/util/url';
import {
  setElasticApiKey,
  setElasticAuthType,
  setElasticPass,
  setElasticPort,
  setElasticUrl,
  setIsElasticSetupValid,
  setElasticUser,
  clearElasticUser,
  clearElasticPass,
  clearElasticApiKey,
  clearElserSettings,
  setElasticManagedIndex,
  setElasticIndex,
  resetListFiles,
  setDraftElasticQueryBodyValue,
  setDraftElasticFilterValue,
  setDraftBodyOptionValue,
  setDraftTitleOptionValue,
  setDraftUrlOptionValue
} from '../../store/reducer';
import {
  selectDraftElasticApiKey,
  selectDraftElasticPass,
  selectDraftElasticPort,
  selectDraftElasticUrl,
  selectDraftElasticUser,
  selectElasticAuthType,
  selectIsElasticSetupValid,
  selectElasticIndexInputMethod,
  selectIsEditing,
  selectPreviouslySavedElasticAuthType
} from '../../store/selectors';
import { messages } from './messages';
import './ElasticSearchInstanceConfig.scss';

interface ElasticSearchInstanceConfigProps {
  noHeader?: boolean;
  readOnly: boolean;
  onClose: () => void;
}
const ELASTICSEARCH_ADD_DOCS_URL =
  generateDocsUrl(
    '/watson-assistant?topic=watson-assistant-search-elasticsearch-add'
  ) + '#setup-elasticsearch';

export const ElasticSearchInstanceConfig = ({
  noHeader = false,
  readOnly,
  onClose
}: ElasticSearchInstanceConfigProps) => {
  const dispatch = useDispatch();
  const formatMsg = useIntl().formatMessage;

  const isEditing = useSelector(selectIsEditing);
  const isElasticSetupValid = useSelector(selectIsElasticSetupValid);

  // TODO ES: If we change ES to be an integration then this should all be move over to use react-hook-form with Yup
  // schema for validation.
  const url = useSelector(selectDraftElasticUrl);
  const [invalidUrl, setInvalidUrl] = useState(false);
  const [invalidUrlText, setInvalidUrlText] = useState('');
  const port = useSelector(selectDraftElasticPort);

  const authType = useSelector(selectElasticAuthType);
  const previouslySavedElasticAuthType = useSelector(
    selectPreviouslySavedElasticAuthType
  );

  const username = useSelector(selectDraftElasticUser);
  const [invalidUsername, setInvalidUsername] = useState(false);
  const [invalidUsernameText, setInvalidUsernameText] = useState('');

  const password = useSelector(selectDraftElasticPass);
  const [invalidPassword, setInvalidPassword] = useState(false);
  const [invalidPasswordText, setInvalidPasswordText] = useState('');

  const apiKey = useSelector(selectDraftElasticApiKey);
  const [invalidApiKey, setInvalidApiKey] = useState(false);
  const [invalidApiKeyText, setInvalidApiKeyText] = useState('');

  const indexInputMethod = useSelector(selectElasticIndexInputMethod);

  const [invalidIndex, setInvalidIndex] = useState(false);

  // If the user had previously configured basic auth then hide the button that says "show password" since there is no
  // password to show (because store doesn't return the password on edit).
  const [hidePasswordEye, setHidePasswordEye] = useState(
    isEditing &&
      previouslySavedElasticAuthType === ELASTIC_SEARCH_AUTH_TYPES.BASIC
  );
  // If the user had previously configured an api key hide the button that says "show password" since there is no api
  // key to show (because store doesn't return the api key on edit).
  const [hideApiKeyEye, setHideApiKeyEye] = useState(
    isEditing &&
      previouslySavedElasticAuthType === ELASTIC_SEARCH_AUTH_TYPES.API
  );

  const urlChange = e => {
    const url = e?.target?.value ?? '';
    if (url === null || _.isEmpty(url?.trim())) {
      setInvalidUrl(true);
      setInvalidUrlText(formatMsg(messages.invalidEmptyValueText));
    } else if (!url.match(HTTP_URL_REGEX)) {
      setInvalidUrl(true);
      setInvalidUrlText(formatMsg(messages.invalidUrlValueText));
    } else {
      setInvalidUrl(false);
      setInvalidUrlText('');
    }
    dispatch(setElasticUrl(url));
  };

  const portChange = e => {
    const port = e?.target?.value ?? '';
    // Port is optional so no validation needed to check that it exists.
    dispatch(setElasticPort(port));
  };

  const usernameChange = e => {
    const username = e?.target?.value ?? '';
    if (username === null || _.isEmpty(username?.trim())) {
      setInvalidUsername(true);
      setInvalidUsernameText(formatMsg(messages.invalidEmptyValueText));
    } else {
      setInvalidUsername(false);
      setInvalidUsernameText('');
    }
    dispatch(setElasticUser(username));
  };

  const passwordChange = e => {
    const password = e?.target?.value ?? '';
    if (password === null || _.isEmpty(password?.trim())) {
      setInvalidPassword(true);
      setInvalidPasswordText(formatMsg(messages.invalidEmptyValueText));
    } else {
      setInvalidPassword(false);
      setInvalidPasswordText('');
    }
    dispatch(setElasticPass(password));
    // Show the password button that says "show password" once the user has typing in the password field.
    setHidePasswordEye(false);
  };

  const apiKeyChange = e => {
    const apiKey = e?.target?.value ?? '';
    if (apiKey === null || _.isEmpty(apiKey?.trim())) {
      setInvalidApiKey(true);
      setInvalidApiKeyText(formatMsg(messages.invalidEmptyValueText));
    } else {
      setInvalidApiKey(false);
      setInvalidApiKeyText('');
    }
    dispatch(setElasticApiKey(apiKey));
    // Show the button that says "show password" once the user has typing in the api key field.
    setHideApiKeyEye(false);
  };

  const changeAuthType = e => {
    const authType = e?.selectedItem;
    dispatch(setElasticAuthType(authType));
    if (authType === ELASTIC_SEARCH_AUTH_TYPES.API) {
      dispatch(clearElasticUser());
      dispatch(clearElasticPass());
    } else if (authType === ELASTIC_SEARCH_AUTH_TYPES.BASIC) {
      dispatch(clearElasticApiKey());
    }
  };

  //Update the config to a new instance of Elastic. Reset the index and managed index state
  const updateConfig = () => {
    dispatch(setIsElasticSetupValid(false));
    dispatch(setElasticIndex(''));
    dispatch(setElasticPort(''));
    dispatch(setElasticManagedIndex(''));
    dispatch(setElasticUser(''));
    dispatch(setElasticUrl(''));
    dispatch(setDraftElasticQueryBodyValue(''));
    dispatch(setDraftElasticFilterValue(''));
    dispatch(setDraftBodyOptionValue(''));
    dispatch(setDraftTitleOptionValue(''));
    dispatch(setDraftUrlOptionValue(''));
    dispatch(resetListFiles());
    dispatch(clearElserSettings());
  };

  // If the index value was previously invalid but the new value is coming from the dropdown then it must be valid so
  // clear the invalid state.
  useEffect(() => {
    if (
      indexInputMethod === ELASTIC_SEARCH_INDEX_INPUT_METHODS.INDEX &&
      invalidIndex
    ) {
      setInvalidIndex(false);
    }
  }, [indexInputMethod, invalidIndex]);

  return (
    <Fragment>
      <div className="ElasticSearchInstanceConfig">
        <div
          className={cx('ElasticSearchInstanceConfig__content-container', {
            'ElasticSearchInstanceConfig__content-container-no-padding':
              noHeader
          })}>
          {!noHeader && (
            <>
              <div className="ElasticSearchInstanceConfig__header">
                <FormattedMessage {...messages.elasticDescription} />
              </div>
              <div className="ElasticSearchInstanceConfig__instruction">
                <FormattedMessage {...messages.description} />
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link--learn-more"
                  href={ELASTICSEARCH_ADD_DOCS_URL}>
                  <FormattedMessage {...messages.learnMore} />
                </Link>
              </div>
            </>
          )}
          <FormGroup
            legendText=""
            className="ElasticSearchInstanceConfig__form">
            <div
              className={cx('', {
                ElasticSearchInstanceConfig__URL: isElasticSetupValid
              })}>
              <TextInput
                autoComplete="off"
                id="ElasticSearchInstanceConfig__form-url-input"
                invalid={invalidUrl}
                invalidText={invalidUrlText}
                placeholder={formatMsg(messages.urlPlaceholder)}
                readOnly={readOnly || isElasticSetupValid}
                labelText={formatMsg(messages.urlLabelText)}
                onChange={urlChange}
                size="md"
                value={url}
                type="text"
              />
              {isElasticSetupValid && (
                <Button
                  className="ElasticSearchInstanceConfig__update-button"
                  kind="secondary"
                  size="md"
                  onClick={updateConfig}>
                  <FormattedMessage {...messages.update} />
                </Button>
              )}
            </div>
            <SslNotification callback={onClose} />
            <TextInput
              autoComplete="off"
              id="ElasticSearchInstanceConfig__form-port-input"
              placeholder={formatMsg(messages.portPlaceholder)}
              readOnly={readOnly}
              labelText={formatMsg(messages.portLabelText)}
              onChange={portChange}
              size="md"
              value={port}
              type="text"
            />
            <br />
            <br />
            <Dropdown
              id="ElasticSearchInstanceConfig__form-auth-type-dropdown"
              label={formatMsg(messages.authTypeLabelText)}
              titleText={formatMsg(messages.authTypeLabelText)}
              items={Object.values(ELASTIC_SEARCH_AUTH_TYPES)}
              selectedItem={authType}
              onChange={changeAuthType}></Dropdown>
            {authType === ELASTIC_SEARCH_AUTH_TYPES.BASIC && (
              <>
                <TextInput
                  autoComplete="off"
                  id="ElasticSearchInstanceConfig__form-username-input"
                  invalid={invalidUsername}
                  invalidText={invalidUsernameText}
                  placeholder={formatMsg(messages.usernamePlaceholder)}
                  readOnly={readOnly}
                  labelText={formatMsg(messages.usernameLabelText)}
                  onChange={usernameChange}
                  size="md"
                  value={username}
                  type="text"
                />
                <PasswordInput
                  // If the user is editing their Elasticsearch setup, and they haven't started typing in a new password,
                  // hide the button that says "show password" since there is no password to show (because store doesn't
                  // return the password on edit). Once the user starts typing in the password field, show the button.
                  className={cx({
                    redactedField: hidePasswordEye
                  })}
                  autoComplete="new-password"
                  id="ElasticSearchInstanceConfig__form-password-input"
                  invalid={invalidPassword}
                  invalidText={invalidPasswordText}
                  placeholder={REDACTED_FIELD}
                  readOnly={readOnly}
                  labelText={formatMsg(messages.passwordLabelText)}
                  onChange={passwordChange}
                  size="md"
                  value={password}
                />
              </>
            )}
            {authType === ELASTIC_SEARCH_AUTH_TYPES.API && (
              <PasswordInput
                // If the user is editing their Elasticsearch setup, and they haven't started typing in a new api key,
                // hide the button that says "show password" since there is no api key to show (because store doesn't
                // return the edit on edit). Once the user starts typing in the api key field, show the button.
                className={cx({
                  redactedField: hideApiKeyEye
                })}
                autoComplete="off"
                id="ElasticSearchInstanceConfig__form-password-input"
                invalid={invalidApiKey}
                invalidText={invalidApiKeyText}
                placeholder={REDACTED_FIELD}
                readOnly={readOnly}
                labelText={formatMsg(messages.apiKeyLabelText)}
                onChange={apiKeyChange}
                size="md"
                value={apiKey}
              />
            )}
          </FormGroup>
        </div>
      </div>
    </Fragment>
  );
};
